import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../../services/api';
import './AdminSections.css';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  
  // Pagination and filtering
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterRole, setFilterRole] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('DESC');

  // UI states
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: currentPage,
        limit: 20,
        sortBy,
        sortOrder
      };
      
      if (filterRole) params.role = filterRole;
      if (searchQuery) params.search = searchQuery;

      const response = await adminAPI.getAllUsers(params);
      setUsers(response.data.users);
      setTotalPages(response.data.pagination.totalPages);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterRole, searchQuery, sortBy, sortOrder]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await adminAPI.getUserStats();
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, [fetchUsers, fetchStats]);

  const handleViewDetails = async (user) => {
    try {
      const response = await adminAPI.getUserById(user.id);
      setSelectedUser(response.data);
      setShowUserDetails(true);
    } catch (err) {
      console.error('Error fetching user details:', err);
      setError(err.response?.data?.error || 'Failed to load user details');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    setActionLoading(userId);
    try {
      await adminAPI.updateUserRole(userId, newRole);
      await fetchUsers();
      await fetchStats();
      if (selectedUser && selectedUser.user.id === userId) {
        setShowUserDetails(false);
        setSelectedUser(null);
      }
    } catch (err) {
      console.error('Error updating role:', err);
      setError(err.response?.data?.error || 'Failed to update user role');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleVerification = async (userId) => {
    setActionLoading(userId);
    try {
      await adminAPI.toggleEmailVerification(userId);
      await fetchUsers();
      if (selectedUser && selectedUser.user.id === userId) {
        const response = await adminAPI.getUserById(userId);
        setSelectedUser(response.data);
      }
    } catch (err) {
      console.error('Error toggling verification:', err);
      setError(err.response?.data?.error || 'Failed to toggle email verification');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteUser = async (userId) => {
    setActionLoading(userId);
    try {
      await adminAPI.deleteUser(userId);
      await fetchUsers();
      await fetchStats();
      setConfirmDelete(null);
      if (selectedUser && selectedUser.user.id === userId) {
        setShowUserDetails(false);
        setSelectedUser(null);
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadgeClass = (role) => {
    switch(role) {
      case 'ADMIN': return 'role-badge role-admin';
      case 'GM': return 'role-badge role-gm';
      case 'PLAYER': return 'role-badge role-player';
      default: return 'role-badge';
    }
  };

  if (loading && !users.length) {
    return (
      <div className="admin-section">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>👥 User Management</h2>
        <p className="section-subtitle">Manage user accounts and permissions</p>
      </div>

      {error && (
        <div className="error-banner">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError(null)} className="error-close">×</button>
        </div>
      )}

      {/* Statistics Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-content">
              <div className="stat-value">{stats.totalUsers}</div>
              <div className="stat-label">Total Users</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🎮</div>
            <div className="stat-content">
              <div className="stat-value">{stats.players}</div>
              <div className="stat-label">Players</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🎲</div>
            <div className="stat-content">
              <div className="stat-value">{stats.gms}</div>
              <div className="stat-label">Game Masters</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⚡</div>
            <div className="stat-content">
              <div className="stat-value">{stats.admins}</div>
              <div className="stat-label">Admins</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <div className="stat-value">{stats.verifiedUsers}</div>
              <div className="stat-label">Verified</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🆕</div>
            <div className="stat-content">
              <div className="stat-value">{stats.newUsersLast7Days}</div>
              <div className="stat-label">New (7 Days)</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="filters-container">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by username or email..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <select 
            value={filterRole} 
            onChange={(e) => {
              setFilterRole(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-select"
          >
            <option value="">All Roles</option>
            <option value="PLAYER">Players</option>
            <option value="GM">Game Masters</option>
            <option value="ADMIN">Admins</option>
          </select>

          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            className="filter-select"
          >
            <option value="createdAt">Sort by Date</option>
            <option value="username">Sort by Username</option>
            <option value="email">Sort by Email</option>
            <option value="role">Sort by Role</option>
          </select>

          <button 
            onClick={() => setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC')}
            className="sort-toggle-btn"
            title={`Sort ${sortOrder === 'ASC' ? 'Descending' : 'Ascending'}`}
          >
            {sortOrder === 'ASC' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Role</th>
              <th>Provider</th>
              <th>Verified</th>
              <th>Joined</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan="7" className="empty-state">
                  No users found
                </td>
              </tr>
            ) : (
              users.map(user => (
                <tr key={user.id}>
                  <td>
                    <div className="user-username">{user.username}</div>
                  </td>
                  <td>
                    <div className="user-email">{user.email}</div>
                  </td>
                  <td>
                    <span className={getRoleBadgeClass(user.role)}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    <span className="provider-badge">
                      {user.provider === 'google' ? '🔗 Google' : '📧 Local'}
                    </span>
                  </td>
                  <td>
                    {user.isEmailVerified ? (
                      <span className="verified-badge">✅ Verified</span>
                    ) : (
                      <span className="unverified-badge">❌ Unverified</span>
                    )}
                  </td>
                  <td>
                    <div className="date-cell">{formatDate(user.createdAt)}</div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleViewDetails(user)}
                        className="btn-icon"
                        title="View Details"
                      >
                        👁️
                      </button>
                      <button
                        onClick={() => setConfirmDelete(user)}
                        className="btn-icon btn-danger"
                        title="Delete User"
                        disabled={actionLoading === user.id}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}

      {/* User Details Modal */}
      {showUserDetails && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowUserDetails(false)}>
          <div className="modal-content user-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>User Details</h3>
              <button onClick={() => setShowUserDetails(false)} className="modal-close">×</button>
            </div>

            <div className="modal-body">
              <div className="user-detail-section">
                <h4>Account Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Username:</span>
                    <span className="detail-value">{selectedUser.user.username}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Email:</span>
                    <span className="detail-value">{selectedUser.user.email}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Role:</span>
                    <span className={getRoleBadgeClass(selectedUser.user.role)}>
                      {selectedUser.user.role}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Provider:</span>
                    <span className="detail-value">
                      {selectedUser.user.provider === 'google' ? '🔗 Google' : '📧 Local'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Email Verified:</span>
                    {selectedUser.user.isEmailVerified ? (
                      <span className="verified-badge">✅ Verified</span>
                    ) : (
                      <span className="unverified-badge">❌ Unverified</span>
                    )}
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Joined:</span>
                    <span className="detail-value">{formatDate(selectedUser.user.createdAt)}</span>
                  </div>
                </div>
              </div>

              <div className="user-detail-section">
                <h4>Campaigns (as GM) - {selectedUser.campaigns.length}</h4>
                {selectedUser.campaigns.length === 0 ? (
                  <p className="empty-message">No campaigns</p>
                ) : (
                  <ul className="entity-list">
                    {selectedUser.campaigns.map(campaign => (
                      <li key={campaign.id}>
                        <span className="entity-name">{campaign.name}</span>
                        <span className="entity-date">{formatDate(campaign.createdAt)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="user-detail-section">
                <h4>Characters - {selectedUser.characters.length}</h4>
                {selectedUser.characters.length === 0 ? (
                  <p className="empty-message">No characters</p>
                ) : (
                  <ul className="entity-list">
                    {selectedUser.characters.map(character => (
                      <li key={character.id}>
                        <span className="entity-name">
                          {character.name} ({character.characterType})
                        </span>
                        <span className="entity-meta">
                          Level {character.level} • {character.availableXp} XP
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="user-detail-section">
                <h4>Admin Actions</h4>
                <div className="admin-actions">
                  <div className="action-group">
                    <label>Change Role:</label>
                    <div className="role-buttons">
                      {['PLAYER', 'GM', 'ADMIN'].map(role => (
                        <button
                          key={role}
                          onClick={() => handleUpdateRole(selectedUser.user.id, role)}
                          disabled={
                            selectedUser.user.role === role || 
                            actionLoading === selectedUser.user.id
                          }
                          className={`role-btn ${selectedUser.user.role === role ? 'active' : ''}`}
                        >
                          {role}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="action-group">
                    <button
                      onClick={() => handleToggleVerification(selectedUser.user.id)}
                      disabled={actionLoading === selectedUser.user.id}
                      className="action-btn"
                    >
                      {selectedUser.user.isEmailVerified ? 'Unverify' : 'Verify'} Email
                    </button>
                  </div>

                  <div className="action-group danger-zone">
                    <label className="danger-label">Danger Zone:</label>
                    <button
                      onClick={() => setConfirmDelete(selectedUser.user)}
                      disabled={actionLoading === selectedUser.user.id}
                      className="action-btn btn-danger"
                    >
                      Delete User Account
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>⚠️ Confirm Deletion</h3>
              <button onClick={() => setConfirmDelete(null)} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              <p className="warning-text">
                Are you sure you want to delete user <strong>{confirmDelete.username}</strong>?
              </p>
              <p className="warning-subtext">
                This will permanently delete:
              </p>
              <ul className="warning-list">
                <li>Their user account</li>
                <li>All their characters</li>
                <li>All campaigns they created (if GM)</li>
                <li>All associated data</li>
              </ul>
              <p className="warning-subtext danger">
                <strong>This action cannot be undone!</strong>
              </p>
            </div>
            <div className="modal-footer">
              <button 
                onClick={() => setConfirmDelete(null)} 
                className="btn-secondary"
                disabled={actionLoading === confirmDelete.id}
              >
                Cancel
              </button>
              <button 
                onClick={() => handleDeleteUser(confirmDelete.id)}
                className="btn-danger"
                disabled={actionLoading === confirmDelete.id}
              >
                {actionLoading === confirmDelete.id ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
